import {
  CATEGORY_LABEL,
  type ScoreEntity,
  WHERE_FIELDS,
  type WhereRelation,
} from '@parcel/filters';
import { cn } from '@parcel/ui';
import { Braces, Link2, List, Plus, SlidersHorizontal, Sparkles } from 'lucide-react';
import * as React from 'react';
import { SearchPill } from '../skills/skill-tree.js';
import { type CardPick, RELATION_CARD_TITLE } from './criteria-state.js';

export interface RailItem {
  group: string;
  label: string;
  pick: CardPick;
}

/** The icon for what a card reads: a field, a linked record, a saved View or a custom condition. */
export function CardIcon({ pick }: { pick: CardPick | { type: 'view' } }) {
  const Icon =
    pick.type === 'relation'
      ? Link2
      : pick.type === 'custom'
        ? Braces
        : pick.type === 'view'
          ? List
          : SlidersHorizontal;
  return <Icon className="size-4 shrink-0 text-muted-foreground" aria-hidden />;
}

/** What the rail offers: the record's own fields by category, its linked records, then Custom condition. */
export function railItems(entity: ScoreEntity): RailItem[] {
  const items: RailItem[] = [];
  for (const field of WHERE_FIELDS[entity]) {
    // A score never reads its own score; linked-record fields live on their relation's card.
    if (field.relation || field.key === 'score') continue;
    items.push({
      group: CATEGORY_LABEL[field.category],
      label: field.label,
      pick: { type: 'field', key: field.key },
    });
  }
  for (const [relation, title] of Object.entries(RELATION_CARD_TITLE[entity]) as [
    WhereRelation,
    { has: string; none?: string },
  ][]) {
    items.push({
      group: 'Linked records',
      label: title.has,
      pick: { type: 'relation', relation, negated: false },
    });
    if (title.none) {
      items.push({
        group: 'Linked records',
        label: title.none,
        pick: { type: 'relation', relation, negated: true },
      });
    }
  }
  items.push({ group: 'Advanced', label: 'Custom condition', pick: { type: 'custom' } });
  return items;
}

/** The left rail: everything a criterion can read, searchable and grouped; a click adds it, Ask adds several. */
export function CriteriaCatalog({
  entity,
  disabled,
  onPick,
  onAsk,
  asking,
}: {
  entity: ScoreEntity;
  disabled?: boolean;
  onPick: (pick: CardPick) => void;
  /** Enables the Ask row: the typed question becomes criteria. */
  onAsk?: (question: string) => void;
  asking?: boolean;
}) {
  const [query, setQuery] = React.useState('');
  const needle = query.trim().toLowerCase();
  const items = railItems(entity).filter(
    (item) =>
      !needle ||
      item.label.toLowerCase().includes(needle) ||
      item.group.toLowerCase().includes(needle),
  );
  const groups = [...new Set(items.map((item) => item.group))];
  const ask = () => {
    if (!onAsk || !needle) return;
    onAsk(query.trim());
    setQuery('');
  };
  return (
    <aside aria-label="Add criteria" className="flex flex-col gap-3 lg:min-h-0 lg:flex-1">
      <h3 className="text-sm font-medium">Add criteria</h3>
      <SearchPill
        label="Search criteria"
        placeholder="Search criteria or ask…"
        value={query}
        onChange={setQuery}
        onEnter={() => {
          if (!disabled && !asking && onAsk && needle) ask();
        }}
      />
      <nav className="-mx-2 min-h-0 flex-1 space-y-3 overflow-y-auto px-2 pb-8">
        {onAsk && needle ? (
          <button
            type="button"
            disabled={disabled || asking}
            onClick={ask}
            className={cn(
              'flex min-h-8 w-full items-center gap-2 rounded-md bg-brand-muted px-2 py-1.5 text-left text-sm',
              'hover:bg-brand-muted/80 disabled:pointer-events-none disabled:opacity-60',
            )}
          >
            <Sparkles className="size-4 shrink-0 text-brand" aria-hidden />
            <span className="font-medium text-brand">{asking ? 'Asking…' : 'Ask:'}</span>
            <span className="truncate">“{query.trim()}”</span>
          </button>
        ) : null}
        {groups.map((group) => (
          <section key={group} className="space-y-0.5">
            <h4 className="px-2 pb-1 text-xs font-medium text-muted-foreground">{group}</h4>
            {items
              .filter((item) => item.group === group)
              .map((item) => (
                <button
                  key={`${group}:${item.label}`}
                  type="button"
                  disabled={disabled}
                  onClick={() => onPick(item.pick)}
                  className={cn(
                    'group flex h-8 w-full items-center gap-2 rounded-md px-2 text-left text-sm',
                    'hover:bg-accent disabled:pointer-events-none disabled:opacity-60',
                  )}
                >
                  <CardIcon
                    pick={
                      item.pick.type === 'field' && item.pick.key === 'view'
                        ? { type: 'view' }
                        : item.pick
                    }
                  />
                  <span className="flex-1 truncate">{item.label}</span>
                  <Plus className="size-3.5 text-muted-foreground opacity-0 group-hover:opacity-100" />
                </button>
              ))}
          </section>
        ))}
        {items.length === 0 && !onAsk ? (
          <p className="px-2 text-xs text-muted-foreground">Nothing matches.</p>
        ) : null}
      </nav>
    </aside>
  );
}
